import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import { inventoryViewKeys } from "../../../api/queryKeys";
import ContainedButton from "../../../components/buttons/ContainedButton";
import TextButton from "../../../components/buttons/TextButton";
import SearchField from "../../../components/filters/SearchField";
import {
  StyledSelect,
  StyledTextField,
} from "../../../components/forms/styles/formFieldStyles";
import LoadingIcon from "../../../components/LoadingIcon";
import { Toast } from "../../../components/Toast";
import SvgToIcon from "../../../icons/SvgToIcon";
import { CustomWidgetPrivacy } from "../../../utils/constants";
import { chartIcons, icons } from "../../stack/environment/icons/iconExport";

const CustomWidgetWizard = ({ open, onClose, onSubmit, isLoading }) => {
  const colors = useColors();
  const theme = useTheme();
  const { getAllInventoryViews } = useAPI();
  const [title, setTitle] = useState("");
  const [widgetType, setWidgetType] = useState("");
  const [selectedViews, setSelectedViews] = useState([]);
  const [description, setDescription] = useState("");
  const [privacy, setPrivacy] = useState(CustomWidgetPrivacy.PUBLIC);
  const [searchQuery, setSearchQuery] = useState("");
  const { data: inventoryViewsResponse } = useQuery({
    queryKey: inventoryViewKeys.singleTenant(),
    queryFn: () => getAllInventoryViews(false),

    select: (response) => response?.data || [],
  });

  const inventoryViews = inventoryViewsResponse || [];
  const filteredViews = inventoryViews.filter((view) =>
    view.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleClose = () => {
    setTitle("");
    setWidgetType("");
    setSelectedViews([]);
    setDescription("");
    setSearchQuery("");
    setPrivacy(CustomWidgetPrivacy.PUBLIC);
    onClose();
  };

  const handleSubmit = () => {
    if (!title || !widgetType || selectedViews.length === 0) {
      Toast.error("Please fill in all required fields");
      return;
    }
    onSubmit({
      widget_title: title,
      widget_type: widgetType,
      description: description,
      queries_to_run: selectedViews,
      is_public: privacy === CustomWidgetPrivacy.PUBLIC,
    });

    handleClose();
  };

  const handleViewToggle = (viewId) => {
    setSelectedViews((prev) =>
      prev.includes(viewId)
        ? prev.filter((id) => id !== viewId)
        : [...prev, viewId]
    );
  };

  const isFormValid = title && widgetType && selectedViews.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle>
        <Typography variant="subtitle1">Create Custom Widget</Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <StyledTextField
            label={<Typography variant="caption">Widget Title</Typography>}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            size="medium"
            required
          />

          <StyledTextField
            label={<Typography variant="caption">Description</Typography>}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            size="medium"
          />

          <FormControl fullWidth required>
            <InputLabel
              sx={{
                fontSize: "0.75rem",
                color: colors.text_secondary,
                "&.MuiInputLabel-shrink": {
                  fontSize: "0.8rem",
                  transform: "translate(14px, -9px) scale(1)",
                },
                "& .MuiInputLabel-asterisk": {
                  color: colors.error_500,
                },
              }}
            >
              Widget Type
            </InputLabel>
            <StyledSelect
              label="Widget Type"
              value={widgetType}
              onChange={(e) => setWidgetType(e.target.value)}
              size="medium"
            >
              <MenuItem value="bar">
                <Box display="flex" alignItems="center" gap={1}>
                  <SvgToIcon
                    IconLogo={chartIcons.BarChartIcon}
                    style={{ width: "20px", height: "20px" }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: colors.text_primary }}
                  >
                    Bar Chart
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="pie">
                <Box display="flex" alignItems="center" gap={1}>
                  <SvgToIcon
                    IconLogo={chartIcons.PieChartIcon}
                    style={{ width: "20px", height: "20px" }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: colors.text_primary }}
                  >
                    Pie Chart
                  </Typography>
                </Box>
              </MenuItem>
            </StyledSelect>
          </FormControl>

          <Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.text_primary,
                fontWeight: 500,
                mb: 1,
                display: "block",
              }}
            >
              Select Views (Queries)
            </Typography>
            <Box sx={{ mb: 1 }}>
              <SearchField
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                colors={colors}
                theme={theme}
              />
            </Box>
            <Box
              sx={{
                maxHeight: "200px",
                overflowY: "auto",
                border: `1px solid ${colors.grey_20percent}`,
                borderRadius: "8px",
                p: 1,
                backgroundColor: colors.common_white,
              }}
            >
              {inventoryViews.length === 0 ? (
                <Typography
                  variant="caption"
                  sx={{ color: colors.text_secondary }}
                >
                  No views available
                </Typography>
              ) : filteredViews.length === 0 ? (
                <Typography
                  variant="caption"
                  sx={{ color: colors.text_secondary }}
                >
                  No views match your search
                </Typography>
              ) : (
                filteredViews.map((view) => (
                  <Box
                    key={view.view_id}
                    onClick={() => handleViewToggle(view.view_id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      borderRadius: "4px",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: colors.grey_8percent,
                      },
                    }}
                  >
                    <Checkbox
                      checked={selectedViews.includes(view.view_id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleViewToggle(view.view_id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ padding: 0, marginRight: "8px" }}
                      icon={
                        <SvgToIcon
                          IconLogo={icons.Unchecked}
                          style={{ width: "20px", height: "20px" }}
                        />
                      }
                      checkedIcon={
                        <SvgToIcon
                          IconLogo={icons.Checked}
                          style={{ width: "20px", height: "20px" }}
                        />
                      }
                    />
                    <Typography variant="caption" color={colors.text_primary}>
                      {view.name}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
            {selectedViews.length > 0 && (
              <Typography
                variant="caption"
                sx={{ color: colors.text_secondary, mt: 0.5, display: "block" }}
              >
                {selectedViews.length} view
                {selectedViews.length !== 1 ? "s" : ""} selected
              </Typography>
            )}
          </Box>
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: colors.text_primary,
                fontFamily: "Poppins, sans-serif",
                fontSize: "12px",
                fontWeight: 500,
                lineHeight: "16px",
                mb: 1,
                display: "block",
              }}
            >
              Privacy settings
            </Typography>
            <RadioGroup
              value={privacy}
              onChange={(e) => setPrivacy(e.target.value)}
              sx={{
                "& .MuiRadio-root": {
                  color: colors.text_secondary,
                  "&.Mui-checked": {
                    color: colors.primary_main,
                  },
                },
                "&.Mui-disabled": {
                  opacity: 0.6,
                },
              }}
            >
              <FormControlLabel
                value="private"
                control={<Radio size="small" />}
                label={
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.text_primary,
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "12px",
                      fontWeight: 400,
                      lineHeight: "16px",
                    }}
                  >
                    Private
                  </Typography>
                }
              />
              <FormControlLabel
                value="public"
                control={<Radio size="small" />}
                label={
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.text_primary,
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "12px",
                      fontWeight: 400,
                      lineHeight: "16px",
                    }}
                  >
                    Public
                  </Typography>
                }
              />
            </RadioGroup>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <TextButton onClick={handleClose} color="inherit" size="small">
          Cancel
        </TextButton>
        <ContainedButton
          onClick={handleSubmit}
          color="primary"
          size="small"
          disabled={!isFormValid}
          loading={isLoading}
        >
          {isLoading ? <LoadingIcon size={12} /> : "Create Widget"}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
};

export default CustomWidgetWizard;
